import {colors} from "@c10h/colors";
import {Testimonial} from "@components/TestimonialSlider";
import {Trans, i18n} from "ni18n";
import React from "react";
import {ImageItem} from "src/components/_common/ImageItemList";
import {QA, SectionDivider} from "src/components/v5/Faq/Faq";
import {BtnProps} from "src/components/v5/V5Comp";
import {useFaqIndex} from "src/hooks/useFaqIndex";

type CarbonForCampusConstants = {
  faq: (QA | SectionDivider)[];
  imageItems: ImageItem[];
  benefits: {
    src: string;
    alt: string;
    header: string;
    list: string[];
    backgroundColor: string;
    pattern: string;
  }[];
  data: {
    header: string | JSX.Element;
    arrowStyle: React.CSSProperties;
    img: string;
    alt: string;
    list: {
      header: string;
      text: string;
    }[];
  }[];
  ctaProps: BtnProps;
  testimonials: Testimonial[];
};

export const useCarbonForCampusConstants = (): CarbonForCampusConstants => {
  const faq = useFaqIndex([
    "How does partnering with Carbon Health healthcare lower costs?",
    "How do you ensure student satisfaction?",
    "Is Carbon Health HIPAA compliant?",
    "Is the Carbon Health app secure?",
    "What protections does Carbon have in place to protect my personal and health information from hackers or outsiders getting into the app or into their databases that store my information?",
  ]);

  const imageItems: ImageItem[] = [
    {
      header: i18n.t("Comprehensive care"),
      content: i18n.t(
        "We offer a modern, flexible solution that integrates primary, urgent, and mental healthcare with nationwide virtual access to providers 7 days a week, 52 weeks a year.",
      ),
      image: {
        src: "/illustrations/doctor-and-patient",
        height: 125,
        alt: i18n.t("Illustration of a doctor and patient discussing the patient’s care."),
      },
    },
    {
      header: i18n.t("Flexible options"),
      content: i18n.t(
        "With our in-person clinics, extended hours, and desktop and mobile apps for virtual care, your students have easy access to high-quality healthcare, whether they’re on campus or learning remotely from a distance. ",
      ),
      image: {
        src: "reason/virtualCare/continuous",
        height: 125,
        alt: i18n.t(
          "Illustration of a young person wearing a face mask and making the peace sign with their hand.",
        ),
      },
    },
    {
      header: i18n.t("Lower costs"),
      content: i18n.t(
        "Symptom management, continuous health monitoring, mental healthcare, and sexual health services can prevent emergencies and more-serious problems from developing down the line, lowering overall costs.",
      ),
      image: {
        src: "/illustrations/piggy-bank",
        height: 125,
        alt: i18n.t("Illustration of a hand dropping a coin into a piggy bank."),
      },
    },
  ];

  const benefits = [
    {
      src: "/illustrations/mobile-app",
      alt: i18n.t("Illustration of a cell phone"),
      header: i18n.t("Comprehensive health services"),
      list: [
        i18n.t("Primary & urgent care"),
        i18n.t("Mental health services"),
        i18n.t("Immunization management"),
        i18n.t("Women's health"),
        i18n.t("LGBTQ+ health"),
        i18n.t("Sexual health"),
      ],
      backgroundColor: colors.lightGreen,
      pattern: "0",
    },

    {
      src: "/illustrations/virtual-care",
      alt: i18n.t("Illustration of a laptop"),
      header: i18n.t("Increased access to healthcare"),
      list: [
        i18n.t("Year-round care"),
        i18n.t("Virtual visits & same-day appointments"),
        i18n.t("Instant access to results and treatment plans in the app"),
        i18n.t("Weekend and extended hours"),
      ],
      backgroundColor: colors.lightBlue,
      pattern: "1",
    },
    {
      src: "/illustrations/syringe-and-vial",
      alt: i18n.t("Illustration of a syringe and vial"),
      header: i18n.t("Immunization management"),
      list: [
        i18n.t("Digital immunization program"),
        i18n.t("Immunization status reports"),
        i18n.t("Vaccine schedule notifications"),
        i18n.t("Health Pass digital vaccine card"),
      ],
      backgroundColor: colors.lightYellow,
      pattern: "2",
    },
    {
      src: "/illustrations/doctor-and-patient",
      alt: i18n.t("Illustration of a doctor and patient"),
      header: i18n.t("Modern, high-quality clinics"),
      list: [
        i18n.t("High-quality and professionally run clinics"),
        i18n.t("Bright, airy spaces"),
        i18n.t("Net Promoter Score (NPS) of 89"),
        i18n.t("Holistic healthcare"),
        i18n.t("Evidence-based medicine"),
      ],
      backgroundColor: colors.lightRed,
      pattern: "3",
    },
    {
      src: "/illustrations/happy-healthy-person-smiling",
      alt: i18n.t("Illustration of a patient receiving virtual care"),
      header: i18n.t("Sexual health and wellness program"),
      list: [
        i18n.t("STI evaluation and tests"),
        i18n.t("Sexual health education"),
        i18n.t("Risk assessment & prevention"),
        i18n.t("Contraceptive options"),
        i18n.t("Same-day access for sexual health issues"),
      ],
      backgroundColor: colors.lightPurple,
      pattern: "4",
    },
    {
      src: "/illustrations/virus",
      alt: i18n.t("Illustration of a virus molecule"),
      header: i18n.t("Complete COVID-19 coverage"),
      list: [
        i18n.t("Testing"),
        i18n.t("Vaccine map"),
        i18n.t("Health Pass digital vaccine card"),
        i18n.t("COVID-Positive care program"),
        i18n.t("Medical consultations"),
        i18n.t("Enterprise dashboard with real-time reporting"),
      ],
      backgroundColor: colors.mint,
      pattern: "5",
    },
  ];

  const data = [
    {
      header: (
        <Trans>
          Return to <span className="darkerYellow">school safely</span>
        </Trans>
      ),
      arrowStyle: {
        left: "30%",
        transform: "rotate(75deg)",
      },
      img: "/illustrations/person-in-chair-with-dog",
      alt: i18n.t(
        "Illustration of a person sitting in a comfy chair with their laptop, while their dog lays happily beside them.",
      ),
      list: [
        {
          header: i18n.t("Digital vaccine tracking program"),
          text: i18n.t(
            "Our program registers students at the beginning of each term and collects their immunization info.",
          ),
        },
        {
          header: i18n.t("Real-time immunization status reports"),
          text: i18n.t(
            "We make status reports available to student affairs administrators for decision-making. Students receive vaccine and immunization notifications, so you don’t have to send out reminders.",
          ),
        },
        {
          header: i18n.t("Health Pass: digital COVID-19 vaccine card"),
          text: i18n.t(
            "Students can verify their vaccine status with our industry-leading, HIPAA-compliant digital vaccine card.",
          ),
        },
      ],
    },
    {
      header: (
        <Trans>
          Support for <span className="darkerYellow">mental health</span>
        </Trans>
      ),
      arrowStyle: {
        left: "35%",
        transform: "rotate(126deg)",
      },
      img: "/illustrations/person-in-virtual-care-session",
      alt: i18n.t(
        "Illustration of a person looking at their smart phone during a video call with their healthcare providers.",
      ),
      list: [
        {
          header: i18n.t("Comprehensive mental health services"),
          text: i18n.t(
            "Our services are accessible to all students regardless of location — services include individual therapy, group therapy, and environmental response.",
          ),
        },
        {
          header: i18n.t("Customized response programs"),
          text: i18n.t(
            "Carbon Health's clinical team will work with you to develop tailored programs in response to on-campus and global events.",
          ),
        },
        {
          header: i18n.t("Access to services from anywhere"),
          text: i18n.t(
            "Carbon Health's comprehensive mental health services are accessible to all students regardless of location.",
          ),
        },
      ],
    },
    {
      header: (
        <Trans>
          Care <span className="darkerYellow">when & where</span> students need it
        </Trans>
      ),
      arrowStyle: {
        left: "5%",
        top: -70,
        transform: "rotateX(180deg) rotate(-80deg)",
      },
      img: "/illustrations/person-on-bike-with-phone",
      alt: i18n.t(
        "Illustration of a person standing with their bicycle, while looking at their phone.",
      ),
      list: [
        {
          header: i18n.t("Access from anywhere on any day"),
          text: i18n.t(
            "We offer nationwide access to coverage 7 days a week, 52 weeks a year. Your remote and distributed students can get care virtually, or walk into any of 73 Carbon Health clinics in 10 states (soon to be 150 clinics around the country).",
          ),
        },
        {
          header: i18n.t("Chat with a provider from any device"),
          text: i18n.t(
            "Our mobile apps are compatible with iOS and Android, and through the app you can chat with your provider, conduct virtual visits, and order prescription refills.",
          ),
        },
      ],
    },
  ];

  const ctaProps = {
    "aria-label": i18n.t("Click to scroll to the contact form."),
    href: "#get-started",
    children: i18n.t("Get Started"),
  };

  const testimonials: Testimonial[] = [
    {
      body: i18n.t(
        "Carbon Health’s virtual and in-person offerings make it easy for students to keep in touch with their healthcare provider, make appointments, and access their medical records through the app.",
      ),
      bg: colors.darkerYellow,
      color: "#fff",
      who: {
        name: "Grace Hum",
        who: i18n.t("Assistant Dean of Students at UC Hastings Law"),
      },
    },
  ];

  return {faq, imageItems, benefits, data, ctaProps, testimonials};
};
